/**
 * Utilidades de RUT (puras, sin dependencias de Vue/vee-validate).
 *
 * El algoritmo del dígito verificador es el MISMO que usa el mixin de la app
 * (src/febos/chile/_vue/mixins/RutMixin.js) para garantizar comportamiento
 * idéntico. Se extrae aquí como funciones puras para reutilizarlo en el
 * form builder (que no usa vee-validate) sin duplicar lógica divergente.
 */

/**
 * Limpia un RUT dejando solo dígitos y K en mayúscula.
 * @param {string} value
 * @returns {string}
 */
export function cleanRut(value) {
  return typeof value === 'string'
    ? value.replace(/[^0-9kK]+/g, '').toUpperCase()
    : '';
}

/**
 * Valida el dígito verificador de un RUT chileno.
 * Implementación idéntica a RutMixin.validateRUT.
 * @param {string} valueRut
 * @returns {boolean}
 */
export function isValidRut(valueRut) {
  if (typeof valueRut !== 'string') {
    return false;
  }
  const valueClean = cleanRut(valueRut);
  // Necesita al menos cuerpo + dígito verificador.
  if (valueClean.length < 2) {
    return false;
  }
  let t = parseInt(valueClean.slice(0, -1), 10);
  if (Number.isNaN(t)) {
    return false;
  }
  let m = 0;
  let s = 1;
  while (t > 0) {
    // eslint-disable-next-line no-plusplus
    s = (s + (t % 10) * (9 - (m++ % 6))) % 11;
    t = Math.floor(t / 10);
  }
  const v = s > 0 ? `${s - 1}` : 'K';
  return v === valueClean.slice(-1);
}

/**
 * Formatea un RUT como "12.345.678-9" (con puntos) o "12345678-9".
 * Mantiene el mismo formateo que RutMixin.formatear.
 * @param {string} value
 * @param {boolean} [dot=true] incluir separadores de miles
 * @returns {string}
 */
export function formatRut(value, dot = true) {
  const clean = cleanRut(value);
  if (!clean) {
    return '';
  }
  if (clean.length === 1) {
    return clean;
  }
  const base = clean.slice(0, -1);
  const check = clean.slice(-1);
  const formattedBase = dot
    ? base.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    : base;
  return `${formattedBase}-${check}`;
}
